/* Filtrous live stock and price. catalog-index.json bakes availability AND
   price at build time, so this shim rewrites both from the Storefront API as
   the index loads (cached 5 min in sessionStorage), and drops anything the
   API does not return. Stock, price and membership all come from Shopify,
   never from a hardcoded list and never from the baked snapshot.

   2026-08-27 (Adelaide) - BACKORDER SPLIT. This used to collapse two different
   Shopify states into one boolean:

       available = availableForSale && !currentlyNotInStock

   currentlyNotInStock means "orderable through continue-selling, but zero on
   hand" - a BACKORDER, not a dead SKU. ANDing it in marked every backordered
   item unavailable, and the app renders !available as "Sold Out": 145 of 600
   catalog SKUs were flagged, of which only 5 were genuinely unavailable. The
   rest were items Shopify would have taken an order for.

   The two states are now carried separately:
     p.available  = availableForSale        -> can this be ordered at all
     p.backorder  = orderable, zero on hand -> "Preorder" rather than "In stock"
   so only availableForSale === false reads as Sold Out. Consumers of
   p.backorder live in filtrous-v3.js (isPreorder) and filtrous-home-families.js
   (hfIsPre); the hardcoded PREORDER lists there remain as an override for SKUs
   that should always read Preorder.

   2026-09-01 (Adelaide) - PRUNE RETIRED SKUS. The merge was
   `k in live && (rewrite)`: a SKU the Storefront API did not return kept its
   BAKED flags. That is backwards. The Storefront API only returns products
   published to the Online Store channel, so "absent from live" is exactly the
   signal that a product has been retired/hidden upstream (Acumatica marks it
   hidden -> the sync unpublishes it in Shopify -> it leaves the Storefront
   API). Keeping the baked row meant a retired part went on rendering in the
   family grid, the search suggestions and the merch rails, badged "In stock",
   linking to a PDP that 404s. ASP-10-0005 sat like that until 2026-09-01, and
   refresh-catalog-index.mjs cannot fix it - it leaves unmatched SKUs untouched
   by design, so the only remedy was hand-editing catalog-index.json.

   Absent from live now means DROPPED from data.products entirely, before
   anything downstream sees it. Everything that browses is derived from that
   array, so the grid row, the search hit, the quick view, the merch rails and
   the recently-viewed card all go at once, on the next page load. The Shopify
   PDP already 404s on its own; the in-app #/p/<sku> route falls back to the
   homepage because maps.bySku no longer has it, and the cart drawer already
   degrades unknown SKUs to a sku-as-title line rather than throwing.

   2026-09-01 later (Adelaide) - LIVE PRICES. Nothing is baked for this any
   more. The sweep already round-trips every published product, so it now
   carries price too and overwrites p.price.

   This closes the last hand-maintained surface in the browse path. The grid
   tile price used to come straight from the baked snapshot, and NOTHING
   repainted it - filtrous-b2b-price.js repaints the PDP and per-customer
   pricing, not the tiles - so a price change in Acumatica left the tile stale
   until someone ran refresh-catalog-index.mjs and pasted the result into the
   theme. On 2026-09-01 that had left three tiles wrong for eighteen days,
   SPP-14-0005 advertised at $157.50 against a real price of $393.75. Plankton
   reports this class of drift every morning as "tile-price drift"; with this
   change there is nothing left for it to find, because the tile and the
   Storefront API are now the same number by construction.

   The Storefront API price is the right source: Plankton's daily scan matches
   it against Acumatica DefaultPrice across ~600 SKUs and finds zero
   mismatches, so live price == Acumatica price. B2B customers are unaffected
   either way, since filtrous-b2b-price.js repaints their prices from the
   ctxprice endpoint after this runs.

   Prices are normalised to two decimals to match the baked format the rest of
   the app expects (money() in filtrous-v3.js parses with parseFloat, so it
   tolerates either, but keeping the shape identical avoids surprises in any
   consumer doing string work).

   Cache key bumped v3 -> v4 because the stored value shape gained the price.

   2026-09-10 (Adelaide) - LIVE TITLES. The title was the last field in the
   browse path still read only from the baked snapshot. Every grid tile, the
   PDP <h1>, the search haystack, the quick view and the merch rails render
   p.title from catalog-index.json, and nothing repainted it - so a rename in
   Acumatica flowed through the sync into the Shopify product title and then
   stopped, invisible on the site. RTK-TN-0200 and RTK-TN-0050 sat as
   "Filter, Racked, Conductive" on every tile and PDP after Acumatica and
   Shopify both said "Non-Filter, Racked".

   The sweep already round-trips every published product, so it now carries
   the product title too and overwrites p.title when it differs. Product
   title, not variant title: the catalog has no multi-variant products, and
   an audit on 2026-09-10 found exactly two rows out of 598 that differed,
   both the RTK-TN tips above. The baked title is now only a first-paint
   hint, like price, stock and membership.

   Not covered here: the page <title> and meta description on /products/<handle>
   come from the product's SEO fields (global.title_tag / description_tag),
   which the Acumatica sync never writes. Those are a product-data fix in
   Shopify, not a theme fix.

   Cache key bumped v4 -> v5 because the stored value shape gained the title.

   Load order matters and is correct in 1.13: catalog-supplement (which pushes
   its EXTRA products in) patches window.fetch before this file, so its
   additions are present when the merge runs and are themselves subject to it
   - an EXTRA entry now gets live price and live stock too, and is dropped if
   it is not published. recently-viewed, stock-badge, fbt, pdp-merch and v3
   all patch after, so they receive the corrected array.

   Three guards keep a bad sweep from emptying the store, since this deletes
   rather than only annotating:
     1. fetchLive returns null unless pagination ran to completion - a
        truncated sweep is no longer usable as evidence of absence.
     2. a live map smaller than MIN_LIVE is treated as a failed sweep.
     3. a prune that would take more than MAX_DROP_RATIO of the index is
        refused outright.
   Any guard tripping falls back to annotate-only, which is also what happens
   when the token is missing or the network fails. Note prices and stock are
   still applied in that fallback - only the deletion is withheld, because a
   partial sweep is bad evidence of absence but perfectly good evidence about
   the products it did return. */
(function () {
  "use strict";
  var CACHE_KEY = "fv3-live-stock-v5",
    TTL_MS = 3e5,
    API = "/api/2025-01/graphql.json",
    MIN_LIVE = 100,
    MAX_DROP_RATIO = 0.25,
    QUERY = "query($after:String){products(first:250,after:$after){pageInfo{hasNextPage endCursor}nodes{title variants(first:5){nodes{sku availableForSale currentlyNotInStock price{amount}}}}}}";
  function token() { try { var el = document.getElementById("shopify-features"); return el && (JSON.parse(el.textContent) || {}).accessToken || null } catch (e) { return null } }
  function cached() { try { var obj = JSON.parse(sessionStorage.getItem(CACHE_KEY)); return obj && obj.map && Date.now() - obj.ts <= TTL_MS ? obj.map : null } catch (e) { return null } }
  function store(map) { try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), map: map })) } catch (e) {} }
  function money(v) { var n = Number(v); return isFinite(n) ? n.toFixed(2) : null }
  function fetchLive(rawFetch) {
    var tok = token();
    if (!tok) return Promise.resolve(null);
    var map = {};
    function page(after, hops) {
      return rawFetch(API, { method: "POST", headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": tok }, body: JSON.stringify({ query: QUERY, variables: { after: after } }) }).then(function (r) { return r.ok ? r.json() : null }).then(function (j) {
        var prods = j && j.data && j.data.products;
        if (!prods) return null;
        prods.nodes.forEach(function (p) {
          p.variants.nodes.forEach(function (v) {
            /* a = orderable at all; b = orderable but nothing on hand (backorder); p = live price; t = live title */
            v.sku && (map[String(v.sku).toUpperCase()] = { a: !!v.availableForSale, b: !!(v.availableForSale && v.currentlyNotInStock), p: v.price && money(v.price.amount), t: p.title || null })
          })
        });
        /* Absence is only meaningful over a COMPLETE sweep. Running out of hops
           mid-catalog returns null rather than a partial map. */
        return prods.pageInfo.hasNextPage ? (hops < 12 ? page(prods.pageInfo.endCursor, hops + 1) : null) : map
      })
    }
    return page(null, 0).catch(function () { return null })
  }
  /* maps.byFamily in filtrous-v3.js is products.filter(p => p.families.indexOf(h) >= 0),
     so the nav counts have to be recomputed the same way or they drift past the
     rows actually rendered. Leaves that empty out stop being linkable. */
  function recount(data) {
    var byFam = {};
    data.products.forEach(function (p) {
      p && p.families && p.families.forEach(function (h) { byFam[h] = (byFam[h] || 0) + 1 })
    });
    (data.departments || []).forEach(function (d) {
      (d.categories || []).forEach(function (c) {
        c.leaves = (c.leaves || []).filter(function (lf) { return (lf.count = byFam[lf.handle] || 0) > 0 })
      });
      d.categories = (d.categories || []).filter(function (c) { return c.leaves.length })
    });
    data.departments = (data.departments || []).filter(function (d) { return d.categories.length });
    typeof data.catalog_sku_count == "number" && (data.catalog_sku_count = data.products.length);
    typeof data.reachable_sku_count == "number" && (data.reachable_sku_count = data.products.length)
  }
  var orig = window.fetch;
  if (orig) {
    var boundOrig = orig.bind(window);
    window.fetch = function (input) {
      var url = typeof input == "string" ? input : input && input.url || "";
      if (String(url).indexOf("catalog-index") === -1) return orig.apply(this, arguments);
      var indexP = orig.apply(this, arguments),
        hit = cached(),
        liveP = hit ? Promise.resolve(hit) : fetchLive(boundOrig).then(function (m) { return m && store(m), m });
      return Promise.all([indexP, liveP]).then(function (rr) {
        var res = rr[0], live = rr[1];
        return !res || !res.ok || !live ? res : res.clone().json().then(function (data) {
          if (!data || !data.products || !data.products.length) return res;
          var total = data.products.length,
            trusted = Object.keys(live).length >= MIN_LIVE,
            kept = [], dropped = [], repriced = 0, retitled = 0;
          data.products.forEach(function (p) {
            var k = p && p.sku ? String(p.sku).toUpperCase() : "";
            if (k && k in live) {
              var L = live[k];
              p.available = L.a;
              p.backorder = L.b;
              if (L.p != null && String(p.price) !== L.p) { p.price = L.p; repriced++ }
              if (L.t && p.title !== L.t) { p.title = L.t; retitled++ }
              kept.push(p)
            }
            else if (!k) kept.push(p); /* no sku to check against; leave it alone */
            else dropped.push(k)
          });
          /* A sweep that would gut the catalog is a broken sweep, not 400 retirements. */
          if (trusted && dropped.length && dropped.length <= total * MAX_DROP_RATIO) {
            data.products = kept;
            recount(data);
            window.console && console.info && console.info("[fv3] dropped " + dropped.length + " SKU(s) not published to the Online Store: " + dropped.join(", "))
          } else if (dropped.length) {
            window.console && console.warn && console.warn("[fv3] live sweep not trusted (" + Object.keys(live).length + " live, " + dropped.length + "/" + total + " would drop); flags and prices applied, nothing pruned")
          }
          repriced && window.console && console.info && console.info("[fv3] repriced " + repriced + " tile(s) from the Storefront API");
          retitled && window.console && console.info && console.info("[fv3] retitled " + retitled + " product(s) from the Storefront API");
          return new Response(JSON.stringify(data), { status: res.status, statusText: res.statusText, headers: { "Content-Type": "application/json" } })
        }).catch(function () { return res })
      })
    }
  }
})();
